import classnames from 'classnames';
import _ from 'lodash';
import 'moment-timezone';
import React from 'react';
import Moment from 'react-moment';
import {connect} from 'react-redux';
import {dropdownLegendOpen} from '../actions';
import Combo from '../components/Combo';
import {show_data_import_time} from '../config/app_settings';
import {geoserverBase} from '../config/environment';
import {INCIDENT_TYPE_KEYS, OVERLAY_KEYS} from '../Constants';
import {shouldCheckIndicatorAge} from '../utils/indicator_utils';
import {formatDefault, isTimeOver12hrs} from '../utils/time_utils';
import './IndicatorLegend.css';

class IndicatorLegend extends React.Component {

  handleToggle = () => {
    const { dispatch, list_open } = this.props;
    dispatch(dropdownLegendOpen(!list_open));
  };

  renderIndicator = (indicator, i) => {
    // The windspeed data is in kts, but the legend is scaled to be km/h
    const units = indicator.units ?
                  `(${indicator.units === 'kts' ? 'km/h' : indicator.units.trim()})` :
                  '';

    let date_created_class = 'c-metadata-list__definition date-updated';
    if (shouldCheckIndicatorAge(indicator) && isTimeOver12hrs(indicator.creationTime)) {
      date_created_class += ' too-old';
    }

    let import_time_class = 'c-metadata-list__definition date-updated';
    if (indicator.importTime && !indicator.exclude_from_age_checks && isTimeOver12hrs(indicator.importTime)) {
      import_time_class += ' too-old';
    }

    return (
      <div className="c-indicator-legend__indicator-key" key={'i' + i}>
        <div
          className="c-indicator-legend__heading">{indicator.displayName} {units}</div>
        {indicator.expandedAbbreviation ?
         <div
           className="c-indicator-legend__subtitle">{indicator.expandedAbbreviation}</div> : null}
        <img
          src={(indicator.base || geoserverBase) + indicator.source + 'REQUEST=GetLegendGraphic&format=image/png&layer='
               + indicator.params.layers}
          className="c-indicator-navigator__legend" id={'indicator-navigator-legend-' + i}
          alt=""/>
        <dl className="c-indicator-legend__metadata c-metadata-list">
          <dt className="c-metadata-list__title">Data updated:</dt>
          <dd className={date_created_class}><Moment tz="Australia/Melbourne"
                                                     format=" dddd DD/MM/YYYY HH:mm ">{indicator.creationTime}</Moment>
          </dd>
          {indicator.importTime && show_data_import_time &&
           <div>
             <dt className="c-metadata-list__title">Data import time:</dt>
             <dd className={import_time_class}><Moment tz="Australia/Melbourne"
                                                       format=" dddd DD/MM/YYYY HH:mm ">{indicator.importTime}</Moment>
             </dd>
           </div>
          }
          <dt className="c-metadata-list__title">Data source:</dt>
          <dd className="c-metadata-list__definition">{indicator.dataSource}</dd>
        </dl>
      </div>
    );
  };

  renderBoundary = (boundary, i) => {
    return (
      <div className="c-indicator-legend__layer-key" key={'b' + i}>
        {boundary.displayName}
        {boundary.type === 'wms' &&
         <img className="c-indicator-legend__layer-image"
              src={boundary.source +
                   'REQUEST=GetLegendGraphic&format=image/png&transparent=true&layer=' +
                   boundary.params.layers}
              id={'layer-navigator-legend-' + i} alt=""/>
        }
        {boundary.type === 'geojson' &&
         <div
           className="c-indicator-legend__layer-image c-indicator-legend__layer-image_geojson-border"
           id={'layer-navigator-legend-' + i}
           style={{
             borderStyle: (boundary.style.dashArray ? 'dashed' : 'solid'),
             borderColor: boundary.style.color
           }}/>
        }
      </div>
    );
  };

  renderOverlay = (overlay, i) => {
    switch (overlay.id) {
      case OVERLAY_KEYS.WEATHER_STATIONS:
        return this.renderAwsLegend(overlay, i);

      case OVERLAY_KEYS.FUEL_MOISTURE_STATIONS:
        return this.renderFmsLegend(overlay, i);

      case OVERLAY_KEYS.WIND_ANIMATED:
      case OVERLAY_KEYS.WIND_BARBS:
      default:
        return undefined;
    }
  };

  renderIncident = (incident) => {
    if (incident.id === INCIDENT_TYPE_KEYS.BURNS) {
      return this.renderBurnsLegend(incident);
    }
    if (incident.id === INCIDENT_TYPE_KEYS.BUSHFIRES) {
      return this.renderBushfireLegend(incident);
    }
    if (incident.id === INCIDENT_TYPE_KEYS.CURRENT_BURNT_AREA) {
      return this.renderCurrentBurntAreaLegend(incident);
    }
    return null;
  };

  renderBurns = (activeIncidents) => {
    const burns = activeIncidents.filter(i => i.id.startsWith('BURNS_'));
    const prescriptionsActive = !!activeIncidents.find(i => i.id === 'INCIDENT_PRESCRIPTIONS');
    if (burns.length > 0) {
      return this.renderBurnsLegend(burns[0], 'Burns', prescriptionsActive);
    }
    return null;
  };

  renderManualWeather = () => {
    return (
      <div className="c-indicator-legend__indicator-key">
        <div className="c-indicator-legend__heading">Manual Weather</div>
        <div className="c-indicator-legend__metadata">
          <div className="manual-weather-icon"/>
          Manual Weather Point
        </div>
      </div>);
  };

  renderBurnsLegend = (layer, headingOverride, showPrescriptions) => {
    return (
      <div className="c-indicator-legend__indicator-key" key={layer.id}>
        <div className="c-indicator-legend__heading">{headingOverride || layer.displayName}</div>
        <div className="c-indicator-legend__metadata">
          <div className="item"><img src={require('../assets/incident_layer/ready.png')} alt="Ready"/>
            Ready
          </div>
          <div className="item"><img src={require('../assets/incident_layer/scheduled.png')} alt="Scheduled"/>
            Scheduled
          </div>
          <div className="item"><img src={require('../assets/incident_layer/ignition_authorised.png')}
                                     alt="Ignition Authorised"/>
            Ignition Authorised
          </div>
          <div className="item"><img src={require('../assets/incident_layer/ignition.png')} alt="Ignition"/>
            Ignition
          </div>
          <div className="item">
            <img src={require('../assets/incident_layer/under_control_-_1.png')} alt="Under Control 1"/>
            Under Control - 1
          </div>
          <div className="item">
            <img src={require('../assets/incident_layer/under_control_-_2.png')} alt="Under Control 2"/>
            Under Control - 2
          </div>
        </div>
        <dl className="c-indicator-legend__metadata c-metadata-list">
          <dt className="c-metadata-list__title">Data updated:</dt>
          <dd className="c-metadata-list__definition"><Moment tz="Australia/Melbourne"
                                                              format=" dddd DD/MM/YYYY HH:mm ">{layer.creationTime}</Moment>
          </dd>
          <dt className="c-metadata-list__title">Data source:</dt>
          <dd className="c-metadata-list__definition">{layer.dataSource}</dd>
        </dl>
        {showPrescriptions &&
         <div className="c-indicator-legend__heading">Prescriptions</div>
        }
        {showPrescriptions &&
         <dl className="c-indicator-legend__metadata c-metadata-list" style={{
           marginBlockStart: 0,
           marginBlockEnd: '0.25em'
         }}>
           <dt className="c-metadata-list__title" style={{ marginTop: 0 }}>Inner
             ring: <span style={{ fontWeight: 'normal' }}>Day 1 Max FDI</span><br/>
             Outer
             ring: <span style={{ fontWeight: 'normal' }}>Wind Speed</span>
           </dt>
         </dl>
        }
        {showPrescriptions &&
         <div className="c-indicator-legend__metadata">
           <div className="item"><img src={require('../assets/incident_layer/inside.png')} alt="Inside"/>
             Within prescription
           </div>
           <div className="item"><img src={require('../assets/incident_layer/close.png')} alt="Close"/>
             Slightly outside prescription ( &lt;10% )
           </div>
           <div className="item"><img src={require('../assets/incident_layer/outside.png')} alt="Outside"/>
             Outside prescription ( &gt;10% )
           </div>
           <div className="item"><img src={require('../assets/incident_layer/missing.png')} alt="Missing"/>
             Prescription not set or data missing
           </div>
         </div>
        }
      </div>);
  };

  renderCurrentBurntAreaLegend = (layer) => {
    return (
      <div className="c-indicator-legend__indicator-key" key={layer.id}>
        <div className="c-indicator-legend__heading">{layer.displayName}</div>
        <div className="c-indicator-legend__metadata">
          <div className="item">
            <span className="c-indicator-legend__burnt-area-swatch"/>
            Current burnt area
          </div>
          <div>Updated: {formatDefault(layer.updated_at)}</div>
        </div>
      </div>);
  };

  renderBushfireLegend = (layer) => {
    return (
      <div className="c-indicator-legend__indicator-key" key={layer.id}>
        <div className="c-indicator-legend__heading">{layer.displayName}</div>
        <div className="c-indicator-legend__metadata">
          <div>Updated: {formatDefault(layer.updated_at)}</div>
          <div className="item"><img src={require('../assets/incident_layer/going.png')} alt="Going"/> Going</div>
          <div className="item"><img src={require('../assets/incident_layer/contained.png')} alt="Contained"/> Contained
          </div>
          <div className="item">
            <img src={require('../assets/incident_layer/controlled.png')} alt="Controlled"/> Controlled
          </div>
        </div>
      </div>);
  };

  renderAwsLegend = (overlay, i) => {
    return (
      <div className="c-indicator-legend__indicator-key" key={'o' + i}>
        <div className="c-indicator-legend__heading">{overlay.displayName}</div>
        <div className="c-indicator-legend__metadata">
          <svg width="20" height="10" viewBox="0 0 10 10">
            <g>
              <path stroke="#3388ff" strokeOpacity="1" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" fill="#3388ff" fillOpacity="0.2"
                    fillRule="evenodd"
                    d="M1,5 a3,3 0 1,0 6,0 a3,3 0 1,0 -6,0 "/>
            </g>
          </svg>
          Automatic Weather Station
        </div>
        <div className="c-indicator-legend__metadata">
          <svg width="20" height="10" viewBox="0 0 10 10">
            <g>
              <path stroke="#33AA33" strokeOpacity="1" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" fill="#33AA33" fillOpacity="0.2"
                    fillRule="evenodd"
                    d="M1,5 a3,3 0 1,0 6,0 a3,3 0 1,0 -6,0 "/>
            </g>
          </svg>
          AWS with Aerological Diagram
        </div>
        <div className="c-indicator-legend__metadata">
          <svg width="20" height="10" viewBox="0 0 10 10">
            <g>
              <path stroke="#8833ff" strokeOpacity="1" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" fill="#8833ff" fillOpacity="0.2"
                    fillRule="evenodd"
                    d="M1,5 a3,3 0 1,0 6,0 a3,3 0 1,0 -6,0 "/>
            </g>
          </svg>
          Non-automatic Weather Station
        </div>
      </div>
    );
  };

  renderFmsLegend = (overlay, i) => {
    return (
      <div className="c-indicator-legend__indicator-key" key={'o' + i}>
        <div className="c-indicator-legend__heading">{overlay.displayName}</div>
        <div className="c-indicator-legend__metadata">
          <svg width="20" height="10" viewBox="0 0 10 10">
            <g>
              <path stroke="#ff3388" strokeOpacity="1" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" fill="#ff3388" fillOpacity="0.2"
                    fillRule="evenodd"
                    d="M1,5 a3,3 0 1,0 6,0 a3,3 0 1,0 -6,0 "/>
            </g>
          </svg>
          Fuel Moisture Station
        </div>
      </div>
    );
  };

  render() {
    const { indicators, boundaries, overlays, incidents, manual_weather, list_open, active_menu } = this.props;

    const active_indicators = indicators.filter(layer => layer.active === true);
    const active_boundaries = boundaries.filter(layer => layer.active === true);
    const active_overlays = overlays.filter(layer => layer.active === true);
    const active_incidents = incidents.filter(layer => layer.active === true);
    const active_manual_weather = 0 !== _.values(manual_weather).length;

    if (0 !== active_indicators.length ||
        0 !== active_boundaries.length ||
        0 !== active_overlays.length ||
        0 !== active_incidents.length ||
        active_manual_weather) {
      return (
        <div className={classnames('c-nav__item', 'legend', { show: 'legend' === active_menu })}>
          <Combo open={list_open} label="Legend" onClick={this.handleToggle} className="print-visible">
            <div className="c-indicator-legend">
              {active_boundaries.length > 0 &&
               <div className="c-indicator-legend__heading">
                 Boundaries
               </div>
              }
              {
                active_boundaries.map((value, i) => this.renderBoundary(value, i))
              }
              {
                active_indicators.map((value, i) => this.renderIndicator(value, i))
              }
              {
                active_overlays.map((value, i) => this.renderOverlay(value, i))
              }
              {
                active_incidents.map((value) => this.renderIncident(value))
              }
              {
                this.renderBurns(active_incidents)
              }
              {
                active_manual_weather && this.renderManualWeather()
              }
            </div>
          </Combo>
        </div>
      );
    } else {
      return null;
    }
  }
}

const mapStateToProps = state => {
  return {
    active_menu: state.view.active_menu,
    indicators: state.indicators.indicators,
    boundaries: state.boundaries.layers,
    overlays: state.overlays.layers,
    incidents: state.incidents.layers,
    list_open: state.view.legend_list_open,
    manual_weather: state.manual_weather.features
  };
};

const mapDispatchToProps = dispatch => {
  return {
    dispatch: dispatch
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(IndicatorLegend);



// WEBPACK FOOTER //
// ./src/containers/IndicatorLegend.js